import { IPasswordValidation, IUsePasswordValidationArgs, IUsePasswordValidationReturn, ValidationRuleType } from "../../../Model/UIComponents.model";
import { useMemo } from "react";
import * as RegularExpressions from "Utils/Global/regularExpressions";
const pluralize = (count: number, singular: string, plural: string) =>
  count === 1 ? singular : plural;

export const usePasswordValidation = ({
  value,
  rules = [],
}: IUsePasswordValidationArgs): IUsePasswordValidationReturn => {
  const handleRules: Record<
    ValidationRuleType,
    {
      getMessage: (req: number) => string;
      validate: (val: string, req: number) => boolean;
    }
  > = {
    [ValidationRuleType.LENGTH]: {
      getMessage: (req) => `${req} or more characters`,
      validate: (val, req) => val.length >= req,
    },
    [ValidationRuleType.DIGITS]: {
      getMessage: (req) => `${req} digit${pluralize(req, '', 's')} required`,
      validate: (val, req) =>
        (val.match(RegularExpressions.Digits) || []).length >= req,
    },
    [ValidationRuleType.SPECIAL_CHARS]: {
      getMessage: (req) =>
        `${req} special character${pluralize(req, '', 's')} required`,
      validate: (val, req) =>
        (val.match(RegularExpressions.SpecialCharacters) || []).length >= req,
    },
    [ValidationRuleType.UPPERCASE]: {
      getMessage: (req) =>
        `${req} uppercase letter${pluralize(req, '', 's')} required`,
      validate: (val, req) =>
        (val.match(RegularExpressions.UppercaseLetters) || []).length >= req,
    },
    [ValidationRuleType.LOWERCASE]: {
      getMessage: (req) =>
        `${req} lowercase letter${pluralize(req, '', 's')} required`,
      validate: (val, req) =>
        (val.match(RegularExpressions.LowercaseLetters) || []).length >= req,
    },
  };

  const results: IPasswordValidation[] = useMemo(() => {
    return rules
      .filter((rule) => handleRules[rule.type])
      .map((rule) => {
        const handler = handleRules[rule.type];
        const isValid = value
          ? handler.validate(value, rule.requiredOccurrences)
          : false;

        return {
          message: rule.message || handler.getMessage(rule.requiredOccurrences),
          isValid,
        };
      });
  }, [value, rules]);

  return { results };
};
